import React, { useState, useEffect } from "react";
import { RiDownloadLine } from "react-icons/ri";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ExcelJS from "exceljs";

import css from "./OrdersDishesComponent.module.css";
import { SliderCalendar } from "../../../ui";
import OrdersDishesListComponent from "../OrdersDishesListComponent/OrdersDishesListComponent";
import { ordersActions } from "../../../store/slices/orders-slices";
import { OrdersInfoSingleDishModal } from "../ordersSectionModals/OrdersInfoSingleDishModal/OrdersInfoSingleDishModal";
import { dishesService } from "../../../services/dishes-services";
import { productsActions } from "../../../store/slices/settings-slices";
import { Select, Space, DatePicker } from "antd";
import dayjs from "dayjs";
import { getDefaultDateRange } from "../../../helpers/getDefaultDateRange";

const { RangePicker } = DatePicker;

const OrdersDishesComponent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDishId, setSelectedDishId] = useState(null);
  const [isShowSingleDish, setIsShowSingleDish] = useState(false);
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const dispatch = useDispatch();
  const dishesData = useSelector((state) => state.orders.dishesOrderByDate);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const { productsArray, trigger } = useSelector((store) => store.products);
  const { dateRange } = useSelector((state) => state.orders);

  useEffect(() => {
    dispatch(productsActions.getAllProducts());
  }, [trigger, dispatch]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}.${month}.${year}`;
  };

  const handleClickXls = async () => {
    if (!dishesData || dishesData.length === 0) {
      alert(t("No dishes data available for the selected date"));
      return;
    }

    // Fetch additional dish data with weights for all dishes
    let enhancedDishesData = [...dishesData];
    try {
      // Get detailed dish information for each dish
      const dishDetailsPromises = dishesData.map((dish) =>
        dishesService
          .getDishById(dish.dishId)
          .then((detailedDish) => {
            return {
              ...dish,
              dishDetails: detailedDish,
            };
          })
          .catch((err) => {
            console.error(
              `Failed to fetch details for dish ${dish.dishId}:`,
              err,
            );
            return dish;
          }),
      );

      enhancedDishesData = await Promise.all(dishDetailsPromises);
      console.log("Enhanced dish data:", enhancedDishesData[0]);
    } catch (error) {
      console.error("Error fetching dish details:", error);
    }

    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    workbook.creator = "GWT ERP";
    workbook.lastModifiedBy = "GWT ERP";
    workbook.created = new Date();
    workbook.modified = new Date();

    // Add Dishes worksheet
    const dishesWorksheet = workbook.addWorksheet("Dishes");

    // Set column widths for Dishes worksheet
    dishesWorksheet.columns = [
      { width: 30 }, // Dish
      { width: 15 }, // Weight (g)
      { width: 25 }, // Dish amount / total product Weight (g)
      { width: 15 }, // Exclude
    ];

    // Add title row for Dishes
    const dishesTitleRow = dishesWorksheet.addRow([
      `Dishes on: ${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`,
    ]);
    dishesTitleRow.font = { bold: true, size: 14 };
    dishesWorksheet.mergeCells("A1:D1");
    dishesWorksheet.getCell("A1").alignment = { horizontal: "center" };
    dishesTitleRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFDA" },
      };
    });

    // Add header row for Dishes
    const dishesHeaderRow = dishesWorksheet.addRow([
      "Dish",
      "Weight (g)",
      "Dish amount / total product Weight (g)",
      "Exclude",
    ]);
    dishesHeaderRow.font = { bold: true, color: { argb: "FFFFFF" } };
    dishesHeaderRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4472C4" },
      };
      cell.alignment = { horizontal: "center" };
    });

    // Add dishes data
    enhancedDishesData.forEach((dish) => {
      const currentLanguage = i18n.language;
      const displayName = dish.dishName?.[currentLanguage] || "";

      // Get dish weight data from the detailed dish info if available
      const dishWeight =
        dish.dishDetails?.weight || dish.dishWeight || dish.weight || "";

      // Get exclude products if any
      const excludeProducts =
        dish.excluded && dish.excluded.length > 0
          ? dish.excluded
              .map((product) => product?.[currentLanguage] || "")
              .join(", ")
          : "";

      const dishRow = dishesWorksheet.addRow([
        `# ${displayName}`,
        dishWeight,
        dish.count || "",
        excludeProducts,
      ]);
      dishRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE5CC" },
        };
      });

      // Add sub-dishes (protein/sauce) rows
      if (dish.subDishes && dish.subDishes.length > 0) {
        dish.subDishes.forEach((subDish) => {
          const subDishName = subDish.dishName?.[currentLanguage] || "";
          const subDishRow = dishesWorksheet.addRow([
            `  ↳ [${subDish.type}] ${subDishName}`,
            subDish.weight || "",
            "",
            "",
          ]);
          subDishRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF2E0" },
            };
            cell.font = { italic: true };
          });
        });
      }
    });

    // Add Ingredients worksheet
    const ingredientsWorksheet = workbook.addWorksheet("Ingredients");

    // Set column widths for Ingredients worksheet
    ingredientsWorksheet.columns = [
      { width: 30 }, // Dish
      { width: 15 }, // Weight (g)
      { width: 25 }, // Dish amount / total product Weight (g)
      { width: 15 }, // Exclude
    ];

    // Add title row for Ingredients
    const ingredientsTitleRow = ingredientsWorksheet.addRow([
      `Dishes on: ${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`,
    ]);
    ingredientsTitleRow.font = { bold: true, size: 14 };
    ingredientsWorksheet.mergeCells("A1:D1");
    ingredientsWorksheet.getCell("A1").alignment = { horizontal: "center" };
    ingredientsTitleRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "E2EFDA" },
      };
    });

    // Add header row for Ingredients
    const ingredientsHeaderRow = ingredientsWorksheet.addRow([
      "Dish",
      "Weight (g)",
      "Dish amount / total product Weight (g)",
      "Exclude",
    ]);
    ingredientsHeaderRow.font = { bold: true, color: { argb: "FFFFFF" } };
    ingredientsHeaderRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "4472C4" },
      };
      cell.alignment = { horizontal: "center" };
    });

    // Add dishes and their ingredients data to Ingredients worksheet
    enhancedDishesData.forEach((dish) => {
      const currentLanguage = i18n.language;
      const displayName = dish.dishName?.[currentLanguage] || "";

      // Get dish weight data from the detailed dish info if available
      const dishWeight =
        dish.dishDetails?.weight || dish.dishWeight || dish.weight || "";

      // Get exclude products if any
      const excludeProducts =
        dish.excluded && dish.excluded.length > 0
          ? dish.excluded
              .map((product) => product?.[currentLanguage] || "")
              .join(", ")
          : "";

      // Add dish row
      const dishRow = ingredientsWorksheet.addRow([
        `# ${displayName}`,
        dishWeight,
        dish.count || "",
        excludeProducts,
      ]);
      dishRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE5CC" },
        };
      });

      // Add sub-dishes (protein/sauce) rows
      if (dish.subDishes && dish.subDishes.length > 0) {
        dish.subDishes.forEach((subDish) => {
          const subDishName = subDish.dishName?.[currentLanguage] || "";
          const subDishRow = ingredientsWorksheet.addRow([
            `  ↳ [${subDish.type}] ${subDishName}`,
            subDish.weight || "",
            "",
            "",
          ]);
          subDishRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF2E0" },
            };
            cell.font = { italic: true };
          });
        });
      }

      // Add ingredient rows if they exist
      if (dish.dishDetails?.products && dish.dishDetails.products.length > 0) {
        console.log("Using dishDetails.products for:", displayName);
        dish.dishDetails.products.forEach((product) => {
          // Debug each product
          console.log("Product structure:", product);

          // Try different paths to get the product name
          const productName =
            product?.name?.[currentLanguage] || // Try direct name access
            product?.name?.["en"] || // Try English fallback
            product?.product?.name?.[currentLanguage] || // Try nested product name
            product?.product?.name?.["en"] || // Try nested English fallback
            "Unknown Product"; // Fallback if no name found

          const productWeight = product.weight || 0;
          const totalProductWeight = productWeight * (dish.count || 1);

          const ingredientRow = ingredientsWorksheet.addRow([
            `${productName}`, // Single space indent for ingredient
            productWeight,
            totalProductWeight,
            "", // Exclude column left empty for ingredients
          ]);
          ingredientRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFFF" },
            };
          });
        });
      } else if (dish.ingredients && dish.ingredients.length > 0) {
        console.log("Using ingredients fallback for:", displayName);
        dish.ingredients.forEach((ingredient) => {
          // Debug each ingredient
          console.log("Ingredient structure:", ingredient);

          const ingredientName =
            ingredient?.name?.[currentLanguage] ||
            ingredient?.name?.["en"] ||
            ingredient?.product?.name?.[currentLanguage] ||
            ingredient?.product?.name?.["en"] ||
            "Unknown Ingredient";

          const ingredientWeight = ingredient.weight || 0;
          const totalIngredientWeight = ingredientWeight * (dish.count || 1);

          const ingredientRow = ingredientsWorksheet.addRow([
            ` ${ingredientName}`, // Single space indent for ingredient
            ingredientWeight,
            totalIngredientWeight,
            "", // Exclude column left empty for ingredients
          ]);
          ingredientRow.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFFF" },
            };
          });
        });
      } else {
        console.log("No ingredients or products found for:", displayName);
      }
    });

    // Add borders to all cells in both worksheets
    [dishesWorksheet, ingredientsWorksheet].forEach((worksheet) => {
      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });
    });

    // Generate the Excel file
    const buffer = await workbook.xlsx.writeBuffer();

    // Create a Blob and download it
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dishes_${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      dispatch(
        ordersActions.setDateRange({
          startDate: dates[0].format("YYYY-MM-DD"),
          endDate: dates[1].format("YYYY-MM-DD"),
        }),
      );
    }
  };

  // Initialize date range and fetch dishes data
  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      const initialRange = getDefaultDateRange(true, true);
      dispatch(ordersActions.setDateRange(initialRange));
    }
  }, [dispatch, dateRange.startDate, dateRange.endDate]);

  // Fetch dishes data when the component mounts or when the selected date changes
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      dispatch(
        ordersActions.getDishesOrderByDate({
          productIds: selectedProductIds,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }),
      );
    }
  }, [dispatch, selectedProductIds, dateRange.startDate, dateRange.endDate]);

  const handleDishClick = (dishId) => {
    setSelectedDishId(dishId);
    setIsShowSingleDish(true);
  };

  return (
    <section className={css.wrap}>
      {/* <div className={css.blockCalendar}>
        <SliderCalendar />
      </div> */}

      <div className={css.blockBody}>
        <div className={css.blockInfo}>
          <div className={css.blockInfoHeader}>
            <Space
              direction="horizontal"
              size="large"
              style={{
                marginBottom: 16,
                padding: "12px 16px",
                background: "#fff",
                borderRadius: 8,
                border: "1px solid #e6e8ee",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <RangePicker
                value={
                  dateRange.startDate && dateRange.endDate
                    ? [dayjs(dateRange.startDate), dayjs(dateRange.endDate)]
                    : null
                }
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                style={{ width: 300 }}
              />

              <Select
                mode="multiple"
                size="middle"
                style={{ width: 300 }}
                placeholder="Please select"
                defaultValue={selectedProductIds}
                onChange={setSelectedProductIds}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={productsArray.map((product) => ({
                  label: product.name[currentLanguage] || product.name["en"],
                  value: product.id,
                }))}
              />
            </Space>
          </div>

          <OrdersDishesListComponent
            setIsOpen={setIsOpen}
            onDishClick={handleDishClick}
          />
        </div>

        <div className={css.blockImport}>
          <button onClick={handleClickXls}>
            xls
            <RiDownloadLine />
          </button>
        </div>
      </div>

      {isOpen && <div>modal here</div>}

      {isShowSingleDish && (
        <OrdersInfoSingleDishModal
          setIsShowSingleDish={setIsShowSingleDish}
          selectedDishId={selectedDishId}
        />
      )}
    </section>
  );
};

export { OrdersDishesComponent };
